console.log('🚀 Module App.tsx evaluating...');

import React, { useState, useCallback, useEffect, Suspense, useRef } from 'react';
import { saveUser, loadUser, clearData } from './services/storage.ts';
import { fetchProfile, saveProfile, updateProfile, updateLocation } from './services/profileApi.ts';
import { fetchReferralData, generateReferralCode } from './services/referralApi.ts';
import { initTelegramApp, getTelegramUser, setBackButton, hapticFeedback } from './services/telegram.ts';
import { Buddy, UserState, ChatMessage, SearchPreferences } from './types.ts';

// Ленивая загрузка компонентов
const BuddyCard = React.lazy(() => import('./components/BuddyCard.tsx'));
const ChatWindow = React.lazy(() => import('./components/ChatWindow.tsx'));
const BottomNav = React.lazy(() => import('./components/BottomNav.tsx'));
const ChatList = React.lazy(() => import('./components/ChatList.tsx'));
const ProfileEditor = React.lazy(() => import('./components/ProfileEditor.tsx'));
const WelcomeScreen = React.lazy(() => import('./components/WelcomeScreen.tsx'));
const SearchFilters = React.lazy(() => import('./components/SearchFilters.tsx'));
const VerificationModal = React.lazy(() => import('./components/VerificationModal.tsx'));
const RatingModal = React.lazy(() => import('./components/RatingModal.tsx'));
const InviteFriendsModal = React.lazy(() => import('./components/InviteFriendsModal.tsx'));
const MapView = React.lazy(() => import('./components/MapView.tsx'));
const UserRatings = React.lazy(() => import('./components/UserRatings.tsx'));
const AgeVerificationModal = React.lazy(() => import('./components/AgeVerificationModal.tsx'));
const CreateProposalModal = React.lazy(() => import('./components/CreateProposalModal.tsx'));
const UserProfileView = React.lazy(() => import('./components/UserProfileView.tsx'));
const ProposalView = React.lazy(() => import('./components/ProposalView.tsx'));
const GlobalChat = React.lazy(() => import('./components/GlobalChat.tsx'));
const MyProposals = React.lazy(() => import('./components/MyProposals.tsx'));
const AdminPanel = React.lazy(() => import('./components/AdminPanel.tsx'));

// Компонент Logo
const Logo = ({ className = "w-12 h-12" }: { className?: string }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 100 100" 
    fill="none" 
    className={className}
  >
    <path 
      d="M25 20 L30 85 C31 92 35 95 42 95 H58 C65 95 69 92 70 85 L75 20" 
      stroke="url(#beerGradient)" 
      strokeWidth="8" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <path 
      d="M32 35 L35 82 C35.5 86 38 88 42 88 H58 C62 88 64.5 86 65 82 L68 35" 
      fill="url(#liquidGradient)" 
      className="animate-pulse"
    />
    <path 
      d="M22 20 C22 12 32 12 35 16 C38 8 50 8 54 14 C58 8 70 8 72 15 C76 12 84 14 78 22" 
      fill="white" 
    />
    <circle cx="45" cy="70" r="2" fill="rgba(255,255,255,0.6)" />
    <circle cx="55" cy="60" r="3" fill="rgba(255,255,255,0.6)" />
    <circle cx="50" cy="50" r="2" fill="rgba(255,255,255,0.6)" />

    <defs>
      <linearGradient id="beerGradient" x1="0" y1="0" x2="100" y2="100">
        <stop offset="0%" stopColor="#FBBF24" />
        <stop offset="100%" stopColor="#D97706" />
      </linearGradient>
      <linearGradient id="liquidGradient" x1="0" y1="0" x2="0" y2="100">
        <stop offset="0%" stopColor="#FCD34D" />
        <stop offset="100%" stopColor="#B45309" />
      </linearGradient>
    </defs>
  </svg>
);

type ViewType = 'search' | 'chats' | 'profile' | 'global-chat';
type OnboardingStep = 'welcome' | 'create' | 'app';

const App: React.FC = () => {
  // -- App State --
  const [onboardingStep, setOnboardingStep] = useState<OnboardingStep>('welcome');
  const [currentView, setCurrentView] = useState<ViewType>('search');

  const [userState, setUserState] = useState<UserState>({
    name: '',
    drinkingPreferences: '',
    mood: 'Хочу поговорить',
    photoBase64: null,
    analysis: null,
    isSearching: false,
    location: null,
    locationError: null,
    notificationsEnabled: true,
    isGuest: false,
    isPremium: false,
    searchPreferences: {
      lookingFor: 'anyone',
      maxDistance: 5,
      onlyVerified: false
    },
    rating: 0,
    isVerified: false
  });

  const [notificationCounts, setNotificationCounts] = useState({
    unreadMessages: 0,
    friendRequests: 0,
    invitations: 0
  });

  const [buddies, setBuddies] = useState<Buddy[]>([]); // Результаты поиска (НЕ включают conversations)
  const [chatBuddies, setChatBuddies] = useState<Buddy[]>([]); // Conversations для вкладки чатов
  const [mapBuddies, setMapBuddies] = useState<Buddy[]>([]); // Все пользователи для карты
  const [activeBuddy, setActiveBuddy] = useState<Buddy | null>(null);
  const [searchType, setSearchType] = useState<'users' | 'ads'>('users'); // Тип поиска: пользователи или предложения
  const searchTypeChangedRef = useRef(false); // Флаг для отслеживания изменения типа поиска
  const hasInitialSearchRun = useRef(false); // Флаг для отслеживания первого поиска
  const [chatHistory, setChatHistory] = useState<Record<string, ChatMessage[]>>({});
  const [isBuddyTyping, setIsBuddyTyping] = useState(false);
  
  // ВАЖНО: Используем telegram_id как ключ для истории чата, а не buddy.id
  // Это обеспечивает, что чаты из предложений и профиля используют одну и ту же историю
  const getChatKey = (telegramId: number | undefined): string => {
    if (!telegramId) return 'unknown';
    return `telegram_${telegramId}`;
  };
  
  // New modals
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [buddyToRate, setBuddyToRate] = useState<Buddy | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showMapView, setShowMapView] = useState(false);
  const [selectLocationMode, setSelectLocationMode] = useState(false);
  const [showUserRatings, setShowUserRatings] = useState(false);
  const [showAgeVerification, setShowAgeVerification] = useState(false);
  const [isAgeVerified, setIsAgeVerified] = useState(false);
  const [showCreateProposalModal, setShowCreateProposalModal] = useState(false);
  const [editingProposal, setEditingProposal] = useState<any | null>(null);
  const [pendingProposalLocation, setPendingProposalLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [viewingProfileTelegramId, setViewingProfileTelegramId] = useState<number | null>(null);
  const [viewingProposal, setViewingProposal] = useState<Buddy | null>(null);
  // Сохраняем состояние формы предложения при выборе локации
  const [pendingProposalData, setPendingProposalData] = useState<{
    photos: string[];
    text: string;
    inventory: string;
    mood: string;
    expires_in_hours?: number;
    meeting_date?: string;
    meeting_time?: string;
    invitedFriendIds?: number[];
  } | null>(null);

  const [myProposals, setMyProposals] = useState<any[]>([]);
  const [isLoadingMyProposals, setIsLoadingMyProposals] = useState(false);

  // Wrapped in useCallback to maintain stable reference
  const fetchMyProposals = useCallback(async (telegramId: number) => {
    setIsLoadingMyProposals(true);
    try {
      const response = await fetch(`/api/ads/my?telegram_id=${telegramId}`);
      const data = await response.json();
      if (data.success) {
        setMyProposals(data.ads);
      }
    } catch (error) {
      console.error('Error fetching my proposals:', error);
    } finally {
      setIsLoadingMyProposals(false);
    }
  }, []);

  useEffect(() => {
    if (userState.telegramId) {
      fetchMyProposals(userState.telegramId);
    }
  }, [userState.telegramId, fetchMyProposals]);

  // -- Initialization & Persistence --
  
  // Helper function to update geolocation
  // Handler for manual location selection from map
  const handleManualLocationSelect = useCallback(async (lat: number, lng: number) => {
    // Если мы в режиме выбора локации для предложения
    if (selectLocationMode) {
      console.log('📍 Локация выбрана для предложения:', { lat, lng });
      
      // Сохраняем локацию (модалка автоматически обновится через currentLocation prop)
      setPendingProposalLocation({ lat, lng });
      
      // Закрываем карту и выключаем режим выбора
      // Модалка остается открытой и автоматически обновит локацию
      setShowMapView(false);
      setSelectLocationMode(false);
      
      hapticFeedback('success');
      return;
    }
    console.log(`📍 Ручное местоположение выбрано: ${lat.toFixed(4)}, ${lng.toFixed(4)}`);
    
    setUserState(prev => ({
      ...prev,
      location: { lat, lng },
      locationError: null
    }));
    
    // Update location on server
    const tgUser = getTelegramUser();
    const myTelegramId = tgUser?.id || userState.telegramId;
    if (myTelegramId) {
      updateLocation(myTelegramId, lat, lng, true)
        .then(() => console.log('✅ Ручное местоположение сохранено на сервере'))
        .catch(err => console.error('❌ Failed to save manual location:', err));
    }
  }, [userState.telegramId]);
  
  const updateGeolocation = useCallback(async (forceRequest: boolean = false) => {
    console.log('📍 Запрос геолокации...');
    
    if (!navigator.geolocation) {
      console.error('❌ Geolocation not supported');
      setUserState(prev => ({ ...prev, locationError: "Геопозиция не поддерживается" }));
      return;
    }

    const tgUser = getTelegramUser();
    const myTelegramId = tgUser?.id || userState.telegramId;
    
    // Не запрашиваем геолокацию для гостей
    if (userState.isGuest) {
      console.log('ℹ️ Пропускаем запрос геолокации для гостя');
      return;
    }

    // Проверяем статус разрешения через Permissions API (если доступен)
    let permissionStatus: PermissionState | null = null;
    if ('permissions' in navigator && 'query' in navigator.permissions) {
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
        permissionStatus = result.state;
        console.log(`🔐 Статус разрешения геолокации: ${permissionStatus}`);
      } catch (err) {
        console.log('⚠️ Permissions API не поддерживается:', err);
      }
    }

    // Проверяем, было ли разрешение дано ранее в БД
    let geolocationGranted = false;
    if (!forceRequest && myTelegramId) {
      try {
        const profile = await fetchProfile(myTelegramId);
        if (profile.user?.geolocation_granted) {
          geolocationGranted = true;
          console.log('✅ Геолокация уже разрешена ранее в БД');
        }
      } catch (err) {
        console.log('⚠️ Не удалось проверить разрешение из БД:', err);
      }
    }

    // Если разрешение уже было дано (в БД или через Permissions API), используем кэш из localStorage
    const cacheKey = `geolocation_cache_${myTelegramId}`;
    const cachedLocation = localStorage.getItem(cacheKey);
    
    if (!forceRequest && (geolocationGranted || permissionStatus === 'granted') && cachedLocation) {
      try {
        const { lat, lng, timestamp } = JSON.parse(cachedLocation);
        const cacheAge = Date.now() - timestamp;
        const maxCacheAge = 30 * 60 * 1000; // 30 минут
        
        if (cacheAge < maxCacheAge) {
          console.log(`✅ Используем кэшированную геолокацию (возраст: ${Math.round(cacheAge / 1000 / 60)} мин)`);
          setUserState(prev => ({
            ...prev,
            location: { lat, lng },
            locationError: null
          }));
          
          // Обновляем геолокацию на сервере в фоне (без показа диалога)
          if (myTelegramId) {
            updateLocation(myTelegramId, lat, lng, true)
              .then(() => console.log('✅ Геолокация обновлена на сервере (из кэша)'))
              .catch(err => console.error('❌ Failed to update location on server:', err));
          }
          return; // Не запрашиваем геолокацию, используем кэш
        } else {
          console.log('ℹ️ Кэш устарел, запрашиваем новую геолокацию');
        }
      } catch (err) {
        console.log('⚠️ Ошибка чтения кэша:', err);
      }
    }

    // Если разрешение уже было дано, используем очень длительный кэш (1 час)
    // Это позволяет не показывать диалог, если браузер уже имеет кэшированную геолокацию
    const maximumAge = (geolocationGranted || permissionStatus === 'granted') ? 3600000 : 300000; // 1 час или 5 минут

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        console.log(`✅ Геолокация получена: ${lat.toFixed(4)}, ${lng.toFixed(4)}`);
        
        // Сохраняем в кэш localStorage
        if (myTelegramId) {
          localStorage.setItem(cacheKey, JSON.stringify({
            lat,
            lng,
            timestamp: Date.now()
          }));
        }
        
        setUserState(prev => ({
          ...prev,
          location: { lat, lng },
          locationError: null
        }));
        
        // Update location on server + save permission status
        // Обновляем местоположение в анкете при каждом входе
        if (myTelegramId) {
          updateLocation(myTelegramId, lat, lng, true) // true = grant permission
            .then(() => console.log('✅ Геолокация и разрешение сохранены на сервере (местоположение в анкете обновлено)'))
            .catch(err => console.error('❌ Failed to update location on server:', err));
        }
      },
      (error) => {
        console.warn("❌ Geolocation error:", error);
        
        // Если разрешение было отклонено, очищаем кэш
        if (error.code === 1) { // PERMISSION_DENIED
          if (myTelegramId) {
            localStorage.removeItem(cacheKey);
            updateLocation(myTelegramId, 0, 0, false).catch(() => {}); // Save denial
          }
          setUserState(prev => ({ ...prev, locationError: "Геопозиция недоступна" }));
        } else {
          // Для других ошибок (timeout, position unavailable) используем кэш, если есть
          if (cachedLocation && !forceRequest) {
            try {
              const { lat, lng } = JSON.parse(cachedLocation);
              console.log('⚠️ Ошибка получения геолокации, используем кэш');
              setUserState(prev => ({
                ...prev,
                location: { lat, lng },
                locationError: null
              }));
              return;
            } catch (err) {
              // Игнорируем ошибку парсинга кэша
            }
          }
          setUserState(prev => ({ ...prev, locationError: "Геопозиция недоступна" }));
        }
      },
      {
        enableHighAccuracy: false, // false = использует кэш браузера
        timeout: 15000,
        maximumAge: maximumAge // Используем длительный кэш, если разрешение уже было дано
      }
    );
  }, [userState.telegramId, userState.isGuest]);

  useEffect(() => {
    // 0. Check age verification
    const checkAgeVerification = () => {
      const verified = localStorage.getItem('age_verified');
      const verifiedDate = localStorage.getItem('age_verified_date');
      
      if (verified === 'true' && verifiedDate) {
        // Проверяем, что подтверждение не старше 30 дней
        const date = new Date(verifiedDate);
        const daysSince = (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24);
        if (daysSince < 30) {
          setIsAgeVerified(true);
          return;
        }
      }
      
      // Если не подтверждено или истекло - показываем модалку
      setShowAgeVerification(true);
    };
    
    checkAgeVerification();
    
    // 1. Init Telegram
    initTelegramApp();

    // 2. Load local storage cache (for offline fallback)
    const tgUser = getTelegramUser();
    
    // Получаем реферальный код из разных источников:
    // 1. Из Telegram Web App start_param (если открыто через кнопку Web App)
    // 2. Из URL параметра ?start=CODE (если открыто напрямую)
    let startParam = window.Telegram?.WebApp?.initDataUnsafe?.start_param;
    
    // Если start_param нет, проверяем URL параметр
    if (!startParam) {
      const urlParams = new URLSearchParams(window.location.search);
      startParam = urlParams.get('start') || null;
    }
    
    if (startParam) {
      console.log('🔗 Реферальный код получен:', startParam);
      // Сохраняем реферальный код для применения при создании профиля
      setUserState(prev => ({ ...prev, referredBy: startParam }));
    }

    // Очищаем buddies при загрузке - данные загружаются только через поиск
    localStorage.removeItem('sobu_buddies_v1');
    
    // НЕ загружаем чаты из localStorage - они теперь в БД!

    // 3. Load profile AND active conversations from server
    const loadProfileFromServer = async () => {
      if (tgUser && tgUser.id) {
        const serverProfile = await fetchProfile(tgUser.id);
        const { fetchFriends } = await import('./services/friendsApi');
        const friendsData = await fetchFriends(tgUser.id);
        const referralData = await fetchReferralData(tgUser.id).catch(() => null);
        
        // Generate referral code if user doesn't have one
        const refCode = serverProfile.user?.referral_code || generateReferralCode(tgUser.id);
        
        if (serverProfile.exists && serverProfile.user) {
          // Server profile exists - load it
          setUserState(prev => ({
            ...prev,
            id: serverProfile.user!.id,
            name: serverProfile.user!.name,
            drinkingPreferences: serverProfile.user!.drinking_preferences || prev.drinkingPreferences,
            mood: serverProfile.ad?.mood || prev.mood,
            photoBase64: null, // We don't store base64 in DB
            photoUrl: serverProfile.user!.photo_url || undefined,
            analysis: serverProfile.user!.profile_analysis || null,
            about: serverProfile.user!.about || undefined,
            city: serverProfile.user!.city || undefined,
            telegramId: tgUser.id,
            location: serverProfile.ad?.lat && serverProfile.ad?.lng ? {
              lat: Number(serverProfile.ad.lat),
              lng: Number(serverProfile.ad.lng)
            } : null,
            isGuest: false,
            isVerified: serverProfile.user!.is_verified || false,
            phoneNumber: serverProfile.user!.phone_number || undefined,
            referralCode: serverProfile.user!.referral_code || refCode,
            referredBy: serverProfile.user!.referred_by || undefined,
            referralCount: serverProfile.user!.referral_count || 0,
            isPremium: serverProfile.user!.is_premium || false,
            premiumUntil: serverProfile.user!.premium_until,
            rating: serverProfile.user!.average_rating || undefined,
            friends: friendsData.friends || []
          }));
          setOnboardingStep('app');
          
          // Автоматически запрашиваем геолокацию для обновления местоположения в анкете (без задержки)
          console.log('📍 Автоматически запрашиваем геолокацию при входе');
          updateGeolocation(false); // false = не принудительно, используем кэш если есть
        } else {
          // No server profile - check local cache or start fresh
          const loadedUser = loadUser();
          if (loadedUser && loadedUser.name) {
            setUserState(prev => ({ 
              ...prev, 
              ...loadedUser,
              referralCode: refCode,
              referralCount: referralData?.referral_count || 0,
              isPremium: referralData?.is_premium || false,
              premiumUntil: referralData?.premium_until
            }));
            setOnboardingStep('app');
          } else {
            // Pre-fill from Telegram
            setUserState(prev => ({ 
              ...prev, 
              name: tgUser.first_name,
              telegramId: tgUser.id,
              referralCode: refCode
            }));
          }
        }
      } else {
        // No telegram user - load from local storage
        const loadedUser = loadUser();
        if (loadedUser && loadedUser.name) {
          setUserState(prev => ({ ...prev, ...loadedUser }));
          setOnboardingStep('app');
        }
      }
    };

    loadProfileFromServer();

    // 4. Load active conversations (chats) from server
    // НЕ добавляем conversations в buddies - они используются только для вкладки чатов
    // и загружаются отдельно при открытии вкладки чатов
    const loadConversations = async () => {
      if (tgUser && tgUser.id) {
        const { fetchConversations } = await import('./services/chatApi');
        const conversations = await fetchConversations(tgUser.id);
        
        if (conversations.length > 0) {
          console.log(`📨 Загружено ${conversations.length} активных чатов из БД (не добавляем в результаты поиска)`);
          
          // Load chat history for each conversation
          const chatHistoryUpdates: Record<string, ChatMessage[]> = {};
          
          await Promise.all(conversations.map(async (buddy) => {
            if (buddy.telegram_id) {
              try {
                // Добавляем mark_read=false, чтобы не помечать сообщения как прочитанные при предварительной загрузке
                const response = await fetch(`/api/chat/history?user1_telegram_id=${tgUser.id}&user2_telegram_id=${buddy.telegram_id}&mark_read=false`);
                const data = await response.json();
                
                if (data.success && data.messages && data.messages.length > 0) {
                  // Convert API messages to ChatMessage format
                  const formattedMessages: ChatMessage[] = data.messages.map((msg: any) => ({
                    id: msg.id?.toString() || `${msg.timestamp}-${Math.random()}`,
                    sender: msg.sender === tgUser.id.toString() ? 'user' : 'buddy',
                    text: msg.text || '',
                    timestamp: msg.timestamp || Date.now(),
                    isRead: msg.is_read || false
                  }));
                  const chatKey = getChatKey(buddy.telegram_id);
                  chatHistoryUpdates[chatKey] = formattedMessages;
                  console.log(`✅ Загружена история для ${buddy.name} (telegram_id: ${buddy.telegram_id}): ${formattedMessages.length} сообщений`);
                } else {
                  const chatKey = getChatKey(buddy.telegram_id);
                  chatHistoryUpdates[chatKey] = [];
                  console.log(`ℹ️ Нет сообщений для ${buddy.name} (telegram_id: ${buddy.telegram_id})`);
                }
              } catch (error) {
                console.error(`❌ Ошибка загрузки истории для ${buddy.name}:`, error);
                const chatKey = getChatKey(buddy.telegram_id);
                chatHistoryUpdates[chatKey] = [];
              }
            }
          }));
          
          // Update chat history state with all loaded conversations
          setChatHistory(prev => ({
            ...prev,
            ...chatHistoryUpdates
          }));
          const chatsWithMessages = Object.values(chatHistoryUpdates).filter(msgs => msgs.length > 0).length;
          console.log(`✅ Обновлена история чатов: ${chatsWithMessages} чатов с сообщениями из ${conversations.length} загруженных`);
        }
      }
    };
    
    loadConversations();

    // 5. Автоматически запрашиваем геолокацию при входе для зарегистрированных пользователей
    // (логика перенесена в loadProfileFromServer, чтобы запрашивать после загрузки профиля)
  }, []);

  // Загружать чаты при переключении на вкладку чатов, если они еще не загружены
  useEffect(() => {
    if (currentView === 'chats') {
      const loadConversationsIfNeeded = async () => {
        const tgUser = getTelegramUser();
        if (tgUser && tgUser.id) {
          // Проверяем, есть ли уже загруженные чаты
          const hasChats = Object.keys(chatHistory).length > 0;
          
          // Если чаты не загружены, загружаем их
          if (!hasChats || chatBuddies.length === 0) {
            console.log('📨 Загружаем чаты при открытии вкладки чатов...');
            try {
              const { fetchConversations } = await import('./services/chatApi');
              const conversations = await fetchConversations(tgUser.id);
              
              if (conversations.length > 0) {
                console.log(`📨 Загружено ${conversations.length} активных чатов из БД`);
                // Сохраняем conversations в отдельное состояние для чатов
                setChatBuddies(conversations);
                
                // Загружаем историю чатов для каждого разговора
                const chatHistoryUpdates: Record<string, ChatMessage[]> = {};
                
                await Promise.all(conversations.map(async (buddy) => {
                  if (buddy.telegram_id) {
                    try {
                      const response = await fetch(`/api/chat/history?user1_telegram_id=${tgUser.id}&user2_telegram_id=${buddy.telegram_id}`);
                      const data = await response.json();
                      
                      if (data.success && data.messages && data.messages.length > 0) {
                        // Convert API messages to ChatMessage format
                        const formattedMessages: ChatMessage[] = data.messages.map((msg: any) => ({
                          id: msg.id?.toString() || `${msg.timestamp}-${Math.random()}`,
                          sender: msg.sender === tgUser.id.toString() ? 'user' : 'buddy',
                          text: msg.text || '',
                          timestamp: msg.timestamp || Date.now()
                        }));
                        chatHistoryUpdates[buddy.id] = formattedMessages;
                        console.log(`✅ Загружена история для ${buddy.name} (id: ${buddy.id}): ${formattedMessages.length} сообщений`);
                      } else {
                        chatHistoryUpdates[buddy.id] = [];
                        console.log(`ℹ️ Нет сообщений для ${buddy.name} (id: ${buddy.id})`);
                      }
                    } catch (error) {
                      console.error(`❌ Ошибка загрузки истории для ${buddy.name}:`, error);
                      chatHistoryUpdates[buddy.id] = [];
                    }
                  }
                }));
                
                // Обновляем историю чатов
                setChatHistory(prev => ({
                  ...prev,
                  ...chatHistoryUpdates
                }));
                const chatsWithMessages = Object.values(chatHistoryUpdates).filter(msgs => msgs.length > 0).length;
                console.log(`✅ Обновлена история чатов: ${chatsWithMessages} чатов с сообщениями из ${conversations.length} загруженных`);
              } else {
                console.log('ℹ️ Нет активных чатов для загрузки');
              }
            } catch (error) {
              console.error('❌ Ошибка загрузки чатов:', error);
            }
          } else {
            console.log('ℹ️ Чаты уже загружены');
          }
        }
      };
      
      loadConversationsIfNeeded();
    }
  }, [currentView, chatHistory, chatBuddies]);

  // НЕ обновляем геолокацию автоматически при возврате в приложение
  // Пользователь может обновить вручную кнопкой GPS при необходимости

  // Persist User only if not guest (or if we want to persist guest session? let's persist guest too for convenience)
  useEffect(() => {
    if (userState.name) saveUser(userState);
  }, [userState]);

  // Polling for notifications
  useEffect(() => {
    if (!userState.telegramId) return;

    const fetchNotifications = async () => {
      try {
        const response = await fetch(`/api/notifications/summary?telegram_id=${userState.telegramId}`);
        const data = await response.json();
        if (data.success) {
          setNotificationCounts({
            unreadMessages: data.unread_messages,
            friendRequests: data.friend_requests,
            invitations: data.invitations
          });
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };

    fetchNotifications(); // Initial fetch
    const interval = setInterval(fetchNotifications, 10000); // Poll every 10 seconds

    return () => clearInterval(interval);
  }, [userState.telegramId]);

  // НЕ сохраняем buddies в localStorage - они должны загружаться только через поиск
  // useEffect(() => {
  //   saveBuddies(buddies);
  // }, [buddies]);

  // Чаты теперь в БД, не нужно сохранять в localStorage
  // useEffect(() => {
  //   saveChats(chatHistory);
  // }, [chatHistory]);

  // Load all users for map view (defined BEFORE useEffect that uses it)
  const loadMapBuddies = useCallback(async (searchTypeForMap: 'users' | 'ads' = 'users') => {
    if (!userState.location) {
      console.log('ℹ️ Нет геолокации для карты');
      return;
    }

    try {
      const params = new URLSearchParams();
      params.append('lat', userState.location.lat.toString());
      params.append('lng', userState.location.lng.toString());
      params.append('max_distance', 'unlimited'); // Все пользователи для карты
      params.append('search_type', searchTypeForMap); // Тип поиска для карты
      
      const tgUser = getTelegramUser();
      if (tgUser?.id || userState.telegramId) {
        params.append('my_telegram_id', (tgUser?.id || userState.telegramId).toString());
      }
      
      console.log(`🗺️ Loading ${searchTypeForMap} for map...`);
      
      const response = await fetch(`/api/search?${params.toString()}`);
      const data = await response.json();
      
      if (data.success && data.buddies) {
        console.log(`🗺️ Loaded ${data.buddies.length} ${searchTypeForMap} for map`);
        setMapBuddies(data.buddies);
      } else {
        setMapBuddies([]);
      }
    } catch (error) {
      console.error("Failed to load map buddies:", error);
      setMapBuddies([]);
    }
  }, [userState.location, userState.telegramId]);

  // Load all users when map is opened (автоматически при открытии карты)
  useEffect(() => {
    if (showMapView) {
      if (userState.location) {
        // По умолчанию загружаем предложения для карты (так как в MapView viewMode = 'ads' по умолчанию)
        loadMapBuddies('ads');
      } else {
        // Если нет геолокации, запрашиваем её
        updateGeolocation(false);
      }
    }
  }, [showMapView, userState.location, loadMapBuddies, updateGeolocation]);
  
  // Загружаем предложения на карте после получения геолокации
  useEffect(() => {
    if (showMapView && userState.location) {
      // По умолчанию загружаем предложения для карты
      loadMapBuddies('ads');
    }
  }, [showMapView, userState.location, loadMapBuddies]);
  
  // Функция поиска (объявлена до использования в useEffect)
  const startSearch = useCallback(async () => {
    hapticFeedback('light');
    setUserState(prev => ({ ...prev, isSearching: true }));
    setCurrentView('search');

    try {
      // Запросить геолокацию перед поиском, если её ещё нет
      if (!userState.location) {
        console.log('ℹ️ Нет геолокации, запрашиваем перед поиском...');
        await updateGeolocation(false); // false = не принудительно
        // Даём время на получение геолокации
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
      
      // Search for real users from database
      const params = new URLSearchParams();
      
      // Add my telegram_id to exclude self from results
      const tgUser = getTelegramUser();
      const myTelegramId = tgUser?.id || userState.telegramId;
      if (myTelegramId) {
        params.append('my_telegram_id', myTelegramId.toString());
      }
      
      if (userState.location) {
        params.append('lat', userState.location.lat.toString());
        params.append('lng', userState.location.lng.toString());
      }
      
      // Отправляем max_distance: число или 'unlimited' для неограниченного радиуса
      if (userState.searchPreferences?.maxDistance !== undefined) {
        params.append('max_distance', userState.searchPreferences.maxDistance.toString());
      } else {
        // Неограниченный радиус
        params.append('max_distance', 'unlimited');
      }
      
      if (userState.searchPreferences?.onlyVerified) {
        params.append('only_verified', 'true');
      }
      
      // Добавляем тип поиска
      params.append('search_type', searchType);
      
      console.log(`Searching for ${searchType} with params:`, params.toString());
      
      const response = await fetch(`/api/search?${params.toString()}`);
      const data = await response.json();
      
      console.log('Search results:', data);
      
      if (data.success && data.buddies) {
        setBuddies(data.buddies);
        
        // Show message if radius was expanded (but not for unlimited radius)
        const isUnlimited = userState.searchPreferences?.maxDistance === undefined;
        if (data.expanded && data.searchRadius && !isUnlimited) {
          const requestedRadius = userState.searchPreferences?.maxDistance || 5;
          console.log(`🔄 Радиус расширен: ${requestedRadius} км → ${data.searchRadius} км`);
          
          // Optional: show Telegram notification
          if (window.Telegram?.WebApp?.showAlert) {
            window.Telegram.WebApp.showAlert(
              `Поблизости найдено мало людей.\nРадиус поиска расширен до ${data.searchRadius} км`
            );
          }
        }
        
        hapticFeedback('success');
        } else {
        // Fallback to AI-generated if no real users found
        console.log('No real users found, falling back to AI generation');
        let photoContext = "";
        
        if (userState.photoBase64 && !userState.analysis && !userState.isGuest) {
          // Динамический импорт для избежания проблем с инициализацией
          const { analyzeUserPhoto } = await import('./services/photoAnalysis.ts');
          const analysis = await analyzeUserPhoto(userState.photoBase64, {
            name: userState.name,
            about: userState.about // Передаем описание профиля для анализа вместе с фото
            // city не нужен для анализа
          });
          photoContext = analysis;
          setUserState(prev => ({ ...prev, analysis }));
          
          // Сохраняем анализ в БД
          if (telegramId) {
            try {
              const { updateProfile } = await import('./services/profileApi.ts');
              await updateProfile({
                telegram_id: telegramId,
                profile_analysis: analysis
              } as any);
            } catch (error) {
              console.error('❌ Ошибка сохранения анализа профиля:', error);
            }
          }
        } else if (userState.analysis) {
          photoContext = userState.analysis;
        }

        // Динамический импорт для избежания проблем с инициализацией @google/genai
        const { findBuddies } = await import('./services/geminiService.ts');
        const results = await findBuddies(
          { name: userState.name, inventory: userState.inventory, mood: userState.mood },
          photoContext,
          userState.location,
          userState.isGuest
        );
        setBuddies(results);
        hapticFeedback('success');
      }

    } catch (error) {
      console.error("Search failed", error);
      hapticFeedback('error');
    } finally {
      setUserState(prev => ({ ...prev, isSearching: false }));
    }
  }, [
    userState.location, 
    userState.telegramId, 
    userState.searchPreferences, 
    userState.photoBase64, 
    userState.analysis, 
    userState.name, 
    userState.drinkingPreferences, 
    userState.mood, 
    userState.about,
    userState.isGuest,
    searchType, 
    updateGeolocation
  ]);
  
  // Автоматически запускаем поиск при изменении типа поиска (только если тип действительно изменился через кнопку)
  useEffect(() => {
    // Пропускаем если поиск уже запущен или тип не менялся через кнопку
    if (!searchTypeChangedRef.current || userState.isSearching) {
      return;
    }
    
    // Сбрасываем флаг после использования
    searchTypeChangedRef.current = false;
    
    if (onboardingStep === 'app' && userState.location) {
      console.log(`🔍 Тип поиска изменился на: ${searchType}, очищаем результаты и запускаем новый поиск...`);
      setBuddies([]); // Очищаем старые результаты
      const timer = setTimeout(() => {
        startSearch();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [searchType]); // Только searchType в зависимостях
  
  // Автоматический поиск при первом открытии приложения (только один раз)
  useEffect(() => {
    if (hasInitialSearchRun.current) return; // Уже запускали
    
    if (onboardingStep === 'app' && !userState.isSearching) {
      // Проверяем, есть ли геолокация
      if (userState.location) {
        console.log('🔍 Автоматический поиск при первом открытии...');
        hasInitialSearchRun.current = true;
        const timer = setTimeout(() => {
          startSearch();
        }, 500);
        return () => clearTimeout(timer);
      } else {
        // Если нет геолокации, запрашиваем её
        console.log('📍 Запрашиваем геолокацию для автоматического поиска...');
        updateGeolocation(false);
      }
    }
  }, [onboardingStep, userState.isSearching, userState.location]);
  
  // Запускаем поиск после получения геолокации при первом открытии (только если еще не запускали)
  useEffect(() => {
    if (hasInitialSearchRun.current) return; // Уже запускали
    
    if (onboardingStep === 'app' && !userState.isSearching && userState.location) {
      console.log('🔍 Запускаем автоматический поиск после получения геолокации...');
      hasInitialSearchRun.current = true;
      const timer = setTimeout(() => {
        startSearch();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [onboardingStep, userState.isSearching, userState.location]);

  // -- Actions --

  const handleGuestLogin = () => {
    hapticFeedback('light');
    setUserState(prev => ({
        ...prev,
        name: 'Тайный Гость',
        mood: 'Наблюдатель',
        drinkingPreferences: 'В поиске',
        isGuest: true,
    }));
    setOnboardingStep('app');
    setTimeout(() => startSearch(), 500); // Auto start search
  };

  const handleCreateProfileStart = () => {
      hapticFeedback('light');
      setOnboardingStep('create');
  };

  const handleCreateProfileSubmit = async () => {
    if (!userState.name) return;
    
    // Проверка: для создания профиля нужны имя и фото
    if (!userState.photoBase64 && !userState.photoUrl) {
      alert('Для создания профиля необходимо загрузить фото');
      hapticFeedback('error');
      return;
    }
    
    // НЕ проверяем геолокацию здесь - профиль создается БЕЗ геолокации
    // Геолокация нужна только для создания объявления, которое создается отдельно
    
    hapticFeedback('success');
    
    const tgUser = getTelegramUser();
    const telegramId = tgUser?.id || userState.telegramId;
    
    if (telegramId) {
      // Prepare photo_base64 with proper format
      let photoBase64 = undefined;
      if (userState.photoBase64) {
        // If photoBase64 doesn't have the data:image prefix, add it
        photoBase64 = userState.photoBase64.startsWith('data:') 
          ? userState.photoBase64 
          : `data:image/jpeg;base64,${userState.photoBase64}`;
      }
      
      console.log('Creating profile with photo:', photoBase64 ? 'YES' : 'NO');
      
      // Сначала создаем профиль (анкету) - только имя и фото
      const result = await saveProfile({
        telegram_id: telegramId,
        name: userState.name,
        username: tgUser?.username,
        photo_url: null,
        photo_base64: photoBase64,
        about: userState.about || undefined,
        city: userState.city || undefined,
        drinking_preferences: userState.drinkingPreferences || undefined
        // Данные объявления создаются отдельно
      });
      
      if (!result.success) {
        console.error('Failed to save profile to server:', result.error);
        alert(result.error || 'Не удалось сохранить профиль');
        hapticFeedback('error');
        return;
      } else {
        console.log('✅ Profile (анкета) created successfully');
        
        // Применяем реферальный код, если он есть (из start_param, userState.referredBy или URL)
        const urlParams = new URLSearchParams(window.location.search);
        const referralCodeToApply = userState.referredBy || 
          window.Telegram?.WebApp?.initDataUnsafe?.start_param || 
          urlParams.get('start');
        
        if (referralCodeToApply && telegramId) {
          try {
            // Проверяем, не использовал ли уже пользователь реферальный код
            // Перезагружаем профиль, чтобы получить актуальные данные (включая referred_by)
            const updatedProfile = await fetchProfile(telegramId);
            if (updatedProfile.exists && updatedProfile.user?.referred_by) {
              console.log('ℹ️ Пользователь уже использовал реферальный код:', updatedProfile.user.referred_by);
              // Не применяем повторно, но обновляем данные реферала
              const { fetchReferralData } = await import('./services/referralApi.ts');
              const referralData = await fetchReferralData(telegramId);
              if (referralData) {
                setUserState(prev => ({
                  ...prev,
                  referralCount: referralData.referral_count || 0,
                  isPremium: referralData.is_premium || false,
                  premiumUntil: referralData.premium_until
                }));
              }
            } else {
              console.log('🔗 Применяем реферальный код:', referralCodeToApply);
              const { applyReferralCode, fetchReferralData } = await import('./services/referralApi.ts');
              const referralResult = await applyReferralCode(telegramId, referralCodeToApply);
              if (referralResult.success) {
                console.log('✅ Реферальный код успешно применен!');
                // Обновляем состояние с новыми данными реферала
                const updatedReferralData = await fetchReferralData(telegramId);
                if (updatedReferralData) {
                  setUserState(prev => ({
                    ...prev,
                    referralCount: updatedReferralData.referral_count || 0,
                    isPremium: updatedReferralData.is_premium || false,
                    premiumUntil: updatedReferralData.premium_until
                  }));
                }
              } else {
                console.warn('⚠️ Не удалось применить реферальный код:', referralResult.error);
              }
            }
          } catch (error) {
            console.error('❌ Ошибка при применении реферального кода:', error);
          }
        }
      }
      
        // Объявление создается отдельно, когда пользователь создаст предложение через модалку
        // Здесь создаем только профиль (анкету)
      }
    
    setUserState(prev => ({ ...prev, isGuest: false }));
    setOnboardingStep('app');
    saveUser({...userState, isGuest: false});
    
    // После создания профиля переключаемся на вкладку профиля
    setCurrentView('profile');
    
    // Не запускаем поиск автоматически - пользователь увидит свой профиль
    // if (buddies.length === 0) {
    //     startSearch();
    // }
  };

  const handleUpdateProfile = async () => {
    hapticFeedback('success');
    
    const tgUser = getTelegramUser();
    const telegramId = tgUser?.id || userState.telegramId;
    
    let savedPhotoUrl = userState.photoUrl;
    
    if (telegramId) {
      // Prepare photo_base64 with proper format
      let photoBase64 = undefined;
      if (userState.photoBase64) {
        // If photoBase64 doesn't have the data:image prefix, add it
        photoBase64 = userState.photoBase64.startsWith('data:') 
          ? userState.photoBase64 
          : `data:image/jpeg;base64,${userState.photoBase64}`;
      }
      
      console.log('Updating profile with photo:', photoBase64 ? 'YES' : 'NO');
      console.log('Photo base64 length:', photoBase64?.length || 0);
      
      // Check if profile already exists (has photoUrl or was previously saved)
      const profileExists = !userState.isGuest && (userState.photoUrl || userState.name);
      
      if (profileExists) {
        // Profile exists - update only profile (not ad) using PUT
        console.log('📝 Updating existing profile only (no ad update)');
        
        // Определяем, было ли фото удалено:
        // Если photoBase64 === null (явно установлено в null, а не undefined) и photoUrl === undefined,
        // и при этом у пользователя был photoUrl до этого (из БД), значит фото было удалено
        const hadPhotoBefore = savedPhotoUrl !== undefined && savedPhotoUrl !== null;
        const photoWasDeleted = userState.photoBase64 === null && userState.photoUrl === undefined && hadPhotoBefore;
        
        // ВАЖНО: Передаем name только если оно не пустое
        // На сервере имя обновится только если оно явно передано и не пустое
        const result = await updateProfile({
          telegram_id: telegramId,
          name: userState.name && userState.name.trim() ? userState.name.trim() : undefined,
          username: tgUser?.username,
          // Don't pass photo_url - server will use existing one from DB or set new one
          // Если photoBase64 === null и было фото до этого, это означает удаление фото
          photo_base64: photoWasDeleted ? null : photoBase64,
          delete_photo: photoWasDeleted,
          about: userState.about,
          city: userState.city,
          drinking_preferences: userState.drinkingPreferences
        });
        
        console.log('Update profile result:', result);
        
        if (!result.success) {
          console.error('❌ Failed to update profile on server:', result.error);
        } else {
          console.log('✅ Profile updated successfully');
          
          // Update photo URL from server response
          if (result.photo_url !== undefined) {
          if (result.photo_url) {
            console.log('📷 Photo saved to server:', result.photo_url);
            savedPhotoUrl = result.photo_url;
            } else {
              // Photo was deleted - clear it
              console.log('🗑️ Photo deleted from server');
            savedPhotoUrl = undefined;
            }
          }
          
          console.log('✅ Профиль успешно обновлён');
        }
      } else {
        // Profile doesn't exist - create profile using POST
        // Проверка: для создания профиля нужны имя и фото
        if (!userState.name || !userState.name.trim()) {
          alert('Для создания профиля необходимо заполнить имя');
          hapticFeedback('error');
          return;
        }
        
        if (!photoBase64 && !userState.photoUrl) {
          alert('Для создания профиля необходимо загрузить фото');
          hapticFeedback('error');
          return;
        }
        
        // НЕ проверяем геолокацию здесь - профиль создается БЕЗ геолокации
        // Геолокация нужна только для создания объявления, которое создается отдельно
        
        console.log('🆕 Creating new profile');
        // Сначала создаем профиль
        const result = await saveProfile({
          telegram_id: telegramId,
          name: userState.name,
          username: tgUser?.username,
          photo_url: userState.photoUrl || null,
          photo_base64: photoBase64,
          about: userState.about || undefined,
          city: userState.city || undefined
          // НЕ передаем данные объявления - они создаются отдельно
        });
        
        // Применяем реферальный код, если он есть (из start_param, userState.referredBy или URL)
        const urlParams = new URLSearchParams(window.location.search);
        const referralCodeToApply = userState.referredBy || 
          window.Telegram?.WebApp?.initDataUnsafe?.start_param || 
          urlParams.get('start');
        
        if (referralCodeToApply && telegramId && result.success) {
          try {
            // Проверяем, не использовал ли уже пользователь реферальный код
            const updatedProfile = await fetchProfile(telegramId);
            if (updatedProfile.exists && updatedProfile.user?.referred_by) {
              console.log('ℹ️ Пользователь уже использовал реферальный код:', updatedProfile.user.referred_by);
              // Не применяем повторно, но обновляем данные реферала
              const { fetchReferralData } = await import('./services/referralApi.ts');
              const referralData = await fetchReferralData(telegramId);
              if (referralData) {
                setUserState(prev => ({
                  ...prev,
                  referralCount: referralData.referral_count || 0,
                  isPremium: referralData.is_premium || false,
                  premiumUntil: referralData.premium_until
                }));
              }
            } else {
              console.log('🔗 Применяем реферальный код:', referralCodeToApply);
              const { applyReferralCode, fetchReferralData } = await import('./services/referralApi.ts');
              const referralResult = await applyReferralCode(telegramId, referralCodeToApply);
              if (referralResult.success) {
                console.log('✅ Реферальный код успешно применен!');
                // Обновляем состояние с новыми данными реферала
                const updatedReferralData = await fetchReferralData(telegramId);
                if (updatedReferralData) {
                  setUserState(prev => ({
                    ...prev,
                    referralCount: updatedReferralData.referral_count || 0,
                    isPremium: updatedReferralData.is_premium || false,
                    premiumUntil: updatedReferralData.premium_until
                  }));
                }
              } else {
                console.warn('⚠️ Не удалось применить реферальный код:', referralResult.error);
              }
            }
          } catch (error) {
            console.error('❌ Ошибка при применении реферального кода:', error);
          }
        }
        
        // Объявление создается отдельно, когда пользователь создаст предложение через модалку
        // Здесь создаем только профиль (анкету)
        
        console.log('Save profile result:', result);
        
        if (!result.success) {
          console.error('❌ Failed to save profile on server:', result.error);
          alert(result.error || 'Не удалось сохранить профиль');
          hapticFeedback('error');
          return;
        } else {
          console.log('✅ Profile and ad created successfully');
          
          // Update photo URL from server response
          if (result.photo_url) {
            console.log('📷 Photo saved to server:', result.photo_url);
            savedPhotoUrl = result.photo_url;
          }
          
          // Reload profile from server to get latest data
          try {
            const serverProfile = await fetchProfile(telegramId);
            if (serverProfile.exists && serverProfile.user) {
              console.log('🔄 Profile reloaded from server');
              // Обновляем photoUrl из сервера (может быть null если удалено)
              savedPhotoUrl = serverProfile.user.photo_url || undefined;
            }
          } catch (reloadError) {
            console.error('⚠️ Failed to reload profile:', reloadError);
          }
        }
      }
    }
    
    // Update state with new photo URL
    setUserState(prev => {
      const updatedState = {
        ...prev, 
        isGuest: false,
        photoUrl: savedPhotoUrl,
        photoBase64: null // Clear base64 after upload
      };
      // Save updated state to localStorage
      saveUser(updatedState);
      return updatedState;
    });
    
    if (window.Telegram?.WebApp?.showAlert) {
        window.Telegram.WebApp.showAlert("Профиль обновлен!");
    } else {
        alert("Профиль обновлен!");
    }
  };

  const handleDeleteProfile = () => {
    // Explicit "Delete" action
    if(window.confirm("Вы уверены? Ваша анкета удалится из поиска, и переписки будут стерты.")) {
        hapticFeedback('medium');
        clearData();
        // Reset state to defaults
        setUserState({
            name: '',
            drinkingPreferences: '',
            mood: 'Хочу поговорить',
            photoBase64: null,
            analysis: null,
            isSearching: false,
            location: null,
            locationError: null,
            notificationsEnabled: true,
            isGuest: false,
            isPremium: false,
        });
        setBuddies([]);
        setChatHistory({});
        setOnboardingStep('welcome');
    }
  }

  // -- Chat & Navigation Logic --

  // Polling for new messages when chat is open
  useEffect(() => {
    if (!activeBuddy || !activeBuddy.telegram_id) return;
    
    const tgUser = getTelegramUser();
    const myTelegramId = tgUser?.id || userState.telegramId;
    if (!myTelegramId) return;
    
    console.log(`🔄 Starting polling for buddy ${activeBuddy.telegram_id}`);
    
    const pollMessages = async () => {
      try {
        const response = await fetch(`/api/chat/history?user1_telegram_id=${myTelegramId}&user2_telegram_id=${activeBuddy.telegram_id}`);
        const data = await response.json();
        
        if (data.success && data.messages) {
          const allMessages: ChatMessage[] = data.messages.map((msg: any) => ({
            id: msg.id.toString(),
            sender: msg.sender === myTelegramId.toString() ? 'user' : 'buddy',
            text: msg.text,
            timestamp: msg.timestamp
          }));
          
          // Update chat history with ALL messages from DB (source of truth)
          // ВАЖНО: Используем telegram_id как ключ, а не buddy.id
          const chatKey = getChatKey(activeBuddy.telegram_id);
          setChatHistory(prev => {
            const currentMessages = prev[chatKey] || [];
            
            // Only update if messages changed
            if (currentMessages.length !== allMessages.length) {
              console.log(`📬 Updated chat: ${currentMessages.length} -> ${allMessages.length} messages`);
              
              // Check for new buddy messages
              const newBuddyMessages = allMessages.filter(msg => 
                msg.sender === 'buddy' && 
                !currentMessages.some(curr => curr.id === msg.id)
              );
              
              if (newBuddyMessages.length > 0) {
                hapticFeedback('light');
              }
              
              return {
                ...prev,
                [chatKey]: allMessages
              };
            }
            
            return prev;
          });
        }
      } catch (error) {
        console.error('❌ Polling error:', error);
      }
    };
    
    // Poll immediately, then every 3 seconds
    pollMessages();
    const interval = setInterval(pollMessages, 3000);
    
    return () => {
      console.log(`⏹️ Stopping polling for buddy ${activeBuddy.telegram_id}`);
      clearInterval(interval);
    };
  }, [activeBuddy]); // Only depend on activeBuddy!

  useEffect(() => {
    const handleBack = () => {
        if (activeBuddy) {
            setActiveBuddy(null);
        }
    };

    if (activeBuddy) {
        setBackButton(true, handleBack);
    } else {
        setBackButton(false);
    }
  }, [activeBuddy]);

  const handleConnect = async (buddy: Buddy) => {
    hapticFeedback('light');
    
    // Если это предложение (объявление), открываем страницу предложения
    if (buddy.type === 'ad') {
      setViewingProposal(buddy);
      return;
    }
    
    // Для обычных пользователей открываем чат
    setActiveBuddy(buddy);
    
    // Load chat history from server
    const tgUser = getTelegramUser();
    const myTelegramId = tgUser?.id || userState.telegramId;
    
    if (myTelegramId && buddy.telegram_id) {
      try {
        console.log(`Loading chat history for buddy ${buddy.telegram_id}...`);
        // Здесь мы явно не передаем mark_read=false, поэтому сообщения будут помечены как прочитанные
        // Это правильное поведение при открытии чата
        const response = await fetch(`/api/chat/history?user1_telegram_id=${myTelegramId}&user2_telegram_id=${buddy.telegram_id}`);
        const data = await response.json();
        
        if (data.success && data.messages) {
          const formattedMessages: ChatMessage[] = data.messages.map((msg: any) => ({
            id: msg.id.toString(),
            sender: msg.sender === myTelegramId.toString() ? 'user' : 'buddy',
            text: msg.text,
            timestamp: msg.timestamp,
            isRead: msg.is_read || false
          }));
          
          // ВАЖНО: Используем telegram_id как ключ для истории чата
          const chatKey = getChatKey(buddy.telegram_id);
          setChatHistory(prev => ({
            ...prev,
            [chatKey]: formattedMessages
          }));
          
          console.log('✅ Chat history loaded:', formattedMessages.length, 'messages');
        } else {
          // If no messages, initialize empty array
          const chatKey = getChatKey(buddy.telegram_id);
          setChatHistory(prev => ({
            ...prev,
            [chatKey]: []
          }));
          console.log('ℹ️ No chat history found, starting fresh');
        }
      } catch (error) {
        console.error('❌ Failed to load chat history:', error);
        // Initialize empty on error
        const chatKey = getChatKey(buddy.telegram_id);
        setChatHistory(prev => ({
          ...prev,
          [chatKey]: []
        }));
      }
    } else {
      // No telegram_id, initialize empty
      const chatKey = getChatKey(buddy.telegram_id);
      setChatHistory(prev => ({
        ...prev,
        [chatKey]: []
      }));
    }
    
    setCurrentView('chats');
  };

  const handleAddFriend = async (buddy: Buddy) => {
    hapticFeedback('light');
    if (!buddy.telegram_id) return;

    const tgUser = getTelegramUser();
    const myTelegramId = tgUser?.id || userState.telegramId;

    if (!myTelegramId) {
      alert('Ошибка: ваш ID не найден');
      return;
    }

    try {
      const response = await fetch('/api/friends/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          telegram_id: myTelegramId,
          target_telegram_id: buddy.telegram_id
        })
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data.success) {
        hapticFeedback('success');
        if (window.Telegram?.WebApp?.showAlert) {
            window.Telegram.WebApp.showAlert('Заявка в друзья отправлена!');
        } else {
            alert('Заявка в друзья отправлена!');
        }
      } else {
        hapticFeedback('error');
        if (window.Telegram?.WebApp?.showAlert) {
            window.Telegram.WebApp.showAlert(data.error || 'Ошибка при отправке заявки');
        } else {
            alert(data.error || 'Ошибка при отправке заявки');
        }
      }
    } catch (error) {
      console.error('Error adding friend:', error);
      hapticFeedback('error');
      const errorMessage = error instanceof Error ? error.message : 'Ошибка сети';
      if (window.Telegram?.WebApp?.showAlert) {
        window.Telegram.WebApp.showAlert(`Ошибка: ${errorMessage}`);
      } else {
        alert(`Ошибка: ${errorMessage}`);
      }
    }
  };

  const handleSendMessage = async (text: string) => {
    if (!activeBuddy || !activeBuddy.telegram_id) return;
    hapticFeedback('light');

    const tgUser = getTelegramUser();
    const myTelegramId = tgUser?.id || userState.telegramId;
    
    if (!myTelegramId) {
      console.error('❌ No telegram_id available');
      return;
    }

    // Проверка: нельзя писать самому себе
    if (Number(myTelegramId) === Number(activeBuddy.telegram_id)) {
      console.error('❌ Cannot send message to yourself');
      hapticFeedback('error');
      if (window.Telegram?.WebApp?.showAlert) {
        window.Telegram.WebApp.showAlert('Нельзя писать сообщения самому себе!');
      } else {
        alert('Нельзя писать сообщения самому себе!');
      }
      return;
    }

    console.log(`📤 Sending message to ${activeBuddy.telegram_id}...`);

    // Send message to server - polling will update UI
    try {
      const response = await fetch('/api/chat/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sender_telegram_id: myTelegramId,
          receiver_telegram_id: activeBuddy.telegram_id,
          text: text
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.message_id) {
        console.log(`✅ Message sent successfully, id: ${data.message_id}`);
        hapticFeedback('success');
        
        // Immediately fetch updated history instead of manually adding
        try {
          const historyResponse = await fetch(`/api/chat/history?user1_telegram_id=${myTelegramId}&user2_telegram_id=${activeBuddy.telegram_id}`);
          const historyData = await historyResponse.json();
          
          if (historyData.success && historyData.messages) {
            const allMessages: ChatMessage[] = historyData.messages.map((msg: any) => ({
              id: msg.id.toString(),
              sender: msg.sender === myTelegramId.toString() ? 'user' : 'buddy',
              text: msg.text,
              timestamp: msg.timestamp
            }));
            
            // ВАЖНО: Используем telegram_id как ключ
            const chatKey = getChatKey(activeBuddy.telegram_id);
            setChatHistory(prev => ({
              ...prev,
              [chatKey]: allMessages
            }));
            
            console.log(`✅ Chat updated with ${allMessages.length} messages`);
          }
        } catch (fetchError) {
          console.error('❌ Failed to fetch updated history:', fetchError);
        }
      } else {
        console.error('❌ Failed to send message:', data.error);
        hapticFeedback('error');
        if (window.Telegram?.WebApp?.showAlert) {
          window.Telegram.WebApp.showAlert(data.error || 'Не удалось отправить сообщение');
        }
      }
    } catch (e) {
      console.error("❌ Failed to send message", e);
      hapticFeedback('error');
      const errorMessage = e instanceof Error ? e.message : 'Ошибка отправки сообщения';
      if (window.Telegram?.WebApp?.showAlert) {
        window.Telegram.WebApp.showAlert(errorMessage);
      }
    }
  };

  const handleCloseChat = () => {
      // Offer to rate after closing chat
      if (activeBuddy) {
        const chatKey = getChatKey(activeBuddy.telegram_id);
        if (chatHistory[chatKey] && chatHistory[chatKey].length > 2) {
        setBuddyToRate(activeBuddy);
        }
      }
      setActiveBuddy(null);
  };

  const handleRatingSubmit = async (rating: number, comment?: string) => {
    if (!buddyToRate) return;
    hapticFeedback('success');
    
    const tgUser = getTelegramUser();
    const myTelegramId = tgUser?.id || userState.telegramId;
    
    if (!myTelegramId || !buddyToRate.telegram_id) {
      console.error('Missing telegram IDs for rating');
      setBuddyToRate(null);
      return;
    }
    
    try {
      console.log('Submitting rating:', { 
        rater: myTelegramId, 
        rated: buddyToRate.telegram_id, 
        rating, 
        comment 
      });
      
      const response = await fetch('/api/rating/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rater_telegram_id: myTelegramId,
          rated_telegram_id: buddyToRate.telegram_id,
          rating,
          comment: comment || null
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { error: errorText || `HTTP ${response.status}` };
        }
        console.error('❌ Rating submission failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        throw new Error(errorData.error || `Failed to submit rating (${response.status})`);
      }
      
      const data = await response.json();
      console.log('✅ Rating submitted successfully:', data);
      
      // Show success message
      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.showAlert(`Спасибо! Рейтинг ${rating} ⭐ отправлен для ${buddyToRate.name}`);
      }
      
    } catch (error) {
      console.error('❌ Failed to submit rating:', error);
      const errorMessage = error instanceof Error ? error.message : 'Неизвестная ошибка';
      console.error('❌ Error message:', errorMessage);
      
      if (window.Telegram?.WebApp) {
        // Показываем более информативное сообщение об ошибке
        let userMessage = 'Ошибка отправки рейтинга. Попробуйте позже.';
        if (errorMessage.includes('User not found')) {
          userMessage = 'Пользователь не найден.';
        } else if (errorMessage.includes('Cannot rate yourself')) {
          userMessage = 'Нельзя оценить самого себя.';
        } else if (errorMessage.includes('Invalid')) {
          userMessage = 'Неверные данные. Проверьте и попробуйте снова.';
        }
        window.Telegram.WebApp.showAlert(userMessage);
      }
    }
    
    setBuddyToRate(null);
  };

  const handleVerificationSubmit = (phoneNumber: string) => {
    hapticFeedback('success');
    setUserState(prev => ({ ...prev, isVerified: true, phoneNumber }));
    
    // TODO: Add API call to verify phone
    // await verifyPhone(phoneNumber);
    
    if (window.Telegram?.WebApp?.showAlert) {
      window.Telegram.WebApp.showAlert("Верификация прошла успешно!");
    } else {
      alert("Верификация прошла успешно!");
    }
  };


  // Handle age verification
  const handleAgeVerification = (isAdult: boolean) => {
    if (isAdult) {
      setIsAgeVerified(true);
      setShowAgeVerification(false);
    } else {
      // Пользователь младше 18 - показываем сообщение и закрываем приложение
      if (window.Telegram?.WebApp?.showAlert) {
        window.Telegram.WebApp.showAlert('К сожалению, наш сервис доступен только для пользователей старше 18 лет.');
      }
      if (window.Telegram?.WebApp?.close) {
        setTimeout(() => window.Telegram.WebApp.close(), 2000);
      }
    }
  };

  const handleCreateProposal = async (data: {
    photos: string[];
    text: string;
    inventory: string;
    mood: string;
    location: { lat: number; lng: number } | null;
    expires_in_hours?: number;
    meeting_date?: string;
    meeting_time?: string;
    invitedFriendIds?: number[];
  }) => {
    if (!userState.telegramId || !data.location) {
      alert('Не удалось получить ваш Telegram ID или местоположение');
      throw new Error('Missing telegramId or location');
    }

    const apiUrl = '/api/ads';
    const apiMethod = 'POST';

    // 1. Upload photos if they are base64
    const uploadedPhotoUrls: string[] = [];
    for (const photo of data.photos) {
      if (photo.startsWith('data:image')) {
        try {
          const uploadResponse = await fetch('/api/upload', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ image: photo }),
          });
          const uploadResult = await uploadResponse.json();
          if (uploadResult.success) {
            uploadedPhotoUrls.push(uploadResult.url);
          }
        } catch (err) {
          console.error('Photo upload failed:', err);
        }
      } else {
        uploadedPhotoUrls.push(photo);
      }
    }

    // 2. Save proposal
    const response = await fetch(apiUrl, {
      method: apiMethod,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        telegram_id: userState.telegramId,
        inventory: data.inventory,
        mood: data.mood,
        description: data.text,
        lat: data.location.lat,
        lng: data.location.lng,
        photos_json: uploadedPhotoUrls,
        expires_in_hours: data.expires_in_hours,
        meeting_date: data.meeting_date,
        meeting_time: data.meeting_time,
        invitedFriendIds: data.invitedFriendIds,
      }),
    });

    if (!response.ok) {
        const errorData = await response.json();
        console.error('Error creating proposal:', errorData);
        alert(`Не удалось создать предложение: ${errorData.message || errorData.error}`);
        throw new Error(errorData.message || 'Failed to create proposal');
    }
    
    // After successful save
    setShowCreateProposalModal(false);
    setEditingProposal(null);
    if (userState.telegramId) {
      fetchMyProposals(userState.telegramId);
    }
    startSearch();
  };

  const handleEditProposal = (proposal: any) => {
    setEditingProposal(proposal);
    setShowCreateProposalModal(true);
  };

  const handleUpdateProposal = async (data: {
    photos: string[];
    text: string;
    inventory: string;
    mood: string;
    location: { lat: number; lng: number } | null;
    expires_in_hours?: number;
    meeting_date?: string;
    meeting_time?: string;
    invitedFriendIds?: number[];
  }) => {
    if (!userState.telegramId || !data.location || !editingProposal) {
      alert('Ошибка: Не удалось найти данные для обновления.');
      throw new Error('Missing telegramId, location, or editingProposal');
    }

    const apiUrl = `/api/ads/${editingProposal.id}`;
    const apiMethod = 'PUT';

    // 1. Upload photos if they are base64
    const uploadedPhotoUrls: string[] = [];
    for (const photo of data.photos) {
      if (photo.startsWith('data:image')) {
        try {
          const uploadResponse = await fetch('/api/upload', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ image: photo }),
          });
          const uploadResult = await uploadResponse.json();
          if (uploadResult.success) {
            uploadedPhotoUrls.push(uploadResult.url);
          }
        } catch (err) {
          console.error('Photo upload failed:', err);
        }
      } else {
        uploadedPhotoUrls.push(photo);
      }
    }

    // 2. Save proposal
    const response = await fetch(apiUrl, {
      method: apiMethod,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        telegram_id: userState.telegramId,
        inventory: data.inventory,
        mood: data.mood,
        description: data.text,
        lat: data.location.lat,
        lng: data.location.lng,
        photos_json: uploadedPhotoUrls,
        expires_in_hours: data.expires_in_hours,
        meeting_date: data.meeting_date,
        meeting_time: data.meeting_time,
        invitedFriendIds: data.invitedFriendIds,
      }),
    });

    if (!response.ok) {
        const errorData = await response.json();
        console.error('Error updating proposal:', errorData);
        alert(`Не удалось обновить предложение: ${errorData.message || errorData.error}`);
        throw new Error(errorData.message || 'Failed to update proposal');
    }
    
    // After successful save
    setShowCreateProposalModal(false);
    setEditingProposal(null);
    if (userState.telegramId) {
      fetchMyProposals(userState.telegramId);
    }
    startSearch();
  };

  const handleDeleteProposal = async (adId: number) => {
    try {
      const response = await fetch(`/api/ads/${adId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ telegram_id: userState.telegramId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete proposal');
      }

      // Предложение удалено успешно - обновляем список
      console.log('✅ Предложение успешно удалено');
      if (userState.telegramId) {
        await fetchMyProposals(userState.telegramId);
      }
      hapticFeedback('success');

    } catch (error) {
      console.error('Error deleting proposal:', error);
      alert(`Ошибка при удалении: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
      hapticFeedback('error');
    }
  };

  // -- Render --

  // 0. Age Verification Screen (must be first)
  if (showAgeVerification && !isAgeVerified) {
    return (
      <Suspense fallback={<div className="min-h-screen bg-dark-900 flex items-center justify-center text-white">Загрузка...</div>}>
        <AgeVerificationModal onVerify={handleAgeVerification} />
      </Suspense>
    );
  }

  // 1. Welcome Screen
  if (onboardingStep === 'welcome') {
      return (
        <Suspense fallback={<div className="min-h-screen bg-dark-900 flex items-center justify-center text-white">Загрузка...</div>}>
          <WelcomeScreen onCreateProfile={handleCreateProfileStart} onGuestSearch={handleGuestLogin} />
        </Suspense>
      );
  }

  // 2. Profile Creation
  if (onboardingStep === 'create') {
    return (
      <div className="min-h-screen bg-dark-900 text-white font-sans flex flex-col p-4 justify-center relative overflow-hidden">
          <div className="absolute top-[-20%] left-[-10%] w-[500px] h-[500px] bg-beer-600/10 rounded-full blur-[100px] pointer-events-none" />
          <Suspense fallback={<div className="text-center text-white">Загрузка редактора...</div>}>
            <ProfileEditor 
              userState={userState} 
              setUserState={setUserState} 
              onSubmit={handleCreateProfileSubmit} 
            />
          </Suspense>
      </div>
    );
  }

  // 3. Main App
  const isChatActive = !!activeBuddy;

  return (
    <div className="min-h-screen bg-dark-900 text-white font-sans relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-[-20%] left-[-10%] w-[500px] h-[500px] bg-beer-600/10 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[500px] h-[500px] bg-purple-900/20 rounded-full blur-[100px] pointer-events-none" />

      {/* Top Header */}
      {!isChatActive && (
        <header className="fixed top-0 left-0 right-0 z-30 bg-dark-900/90 backdrop-blur-md flex justify-between items-center px-6 py-4 border-b border-gray-800/50 transition-all">
            <div className="flex items-center gap-2">
                <div className="relative">
                    <Logo className="w-8 h-8" />
                    {userState.isPremium && (
                        <div className="absolute -top-1 -right-1 text-yellow-500 drop-shadow-[0_0_5px_rgba(234,179,8,0.8)]">
                             <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                        </div>
                    )}
                </div>
                <div>
                    <h1 className="text-xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-300 leading-none">
                    SOBU
                    </h1>
                    {userState.location && (
                    <div className="flex items-center gap-1 text-[9px] text-green-400 font-mono uppercase">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                        GPS: ON
                    </div>
                    )}
                </div>
            </div>
            {currentView === 'search' && (
                <div className="flex gap-2">
                    <button 
                        onClick={() => {
                            hapticFeedback('light');
                            updateGeolocation();
                        }} 
                        className="bg-dark-800 hover:bg-dark-700 p-2 rounded-full border border-gray-700 transition-colors active:scale-90 shadow-lg"
                        title="Обновить геолокацию"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${userState.location ? 'text-green-400' : 'text-red-400'}`}>
                          <circle cx="12" cy="12" r="10"></circle>
                          <circle cx="12" cy="12" r="4"></circle>
                          <line x1="12" y1="2" x2="12" y2="4"></line>
                          <line x1="12" y1="20" x2="12" y2="22"></line>
                          <line x1="2" y1="12" x2="4" y2="12"></line>
                          <line x1="20" y1="12" x2="22" y2="12"></line>
                        </svg>
                    </button>
                    <button onClick={() => setShowFiltersModal(true)} className="bg-dark-800 hover:bg-dark-700 p-2 rounded-full border border-gray-700 transition-colors active:scale-90 shadow-lg">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                        </svg>
                    </button>
                    <button 
                        onClick={() => {
                          hapticFeedback('light');
                          setShowMapView(true);
                          setSelectLocationMode(false);
                        }} 
                        className="bg-dark-800 hover:bg-dark-700 p-2 rounded-full border border-gray-700 transition-colors active:scale-90 shadow-lg"
                        title="Показать на карте"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                          <circle cx="12" cy="10" r="3"></circle>
                        </svg>
                    </button>
                    <button onClick={startSearch} className="bg-dark-800 hover:bg-dark-700 p-2 rounded-full border border-gray-700 transition-colors active:scale-90 shadow-lg">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${userState.isSearching ? 'animate-spin text-beer-500' : 'text-white'}`}><path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/></svg>
                    </button>
                </div>
            )}
        </header>
      )}

      {/* Admin Panel - доступен по /admin */}
      {window.location.pathname === '/admin' && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"><div className="text-white">Загрузка админ-панели...</div></div>}>
          <AdminPanel />
        </Suspense>
      )}

      <main className={`pt-24 px-4 max-w-md mx-auto min-h-screen pb-24 transition-all duration-300 ${isChatActive ? 'scale-95 opacity-50 pointer-events-none' : 'scale-100 opacity-100'}`}>
        
        {/* --- SEARCH VIEW --- */}
        {currentView === 'search' && window.location.pathname !== '/admin' && (
            <>
                {/* Переключатель типа поиска */}
                {!userState.isSearching && (
                  <div className="mb-6 flex gap-2 bg-dark-800/50 p-1 rounded-xl border border-gray-700/50">
                    <button
                      onClick={() => {
                        if (searchType === 'users') return; // Уже выбран
                        hapticFeedback('light');
                        setBuddies([]); // Очищаем результаты при смене типа
                        searchTypeChangedRef.current = true; // Помечаем, что тип изменился
                        setSearchType('users');
                        // Поиск запустится автоматически через useEffect при изменении searchType
                      }}
                      className={`flex-1 py-2.5 px-4 rounded-lg font-bold text-sm transition-all ${
                        searchType === 'users'
                          ? 'bg-beer-500 text-black shadow-lg'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      👤 Пользователи
                    </button>
                    <button
                      onClick={() => {
                        if (searchType === 'ads') return; // Уже выбран
                        hapticFeedback('light');
                        setBuddies([]); // Очищаем результаты при смене типа
                        searchTypeChangedRef.current = true; // Помечаем, что тип изменился
                        setSearchType('ads');
                        // Поиск запустится автоматически через useEffect при изменении searchType
                      }}
                      className={`flex-1 py-2.5 px-4 rounded-lg font-bold text-sm transition-all ${
                        searchType === 'ads'
                          ? 'bg-beer-500 text-black shadow-lg'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      📝 Предложения
                    </button>
                  </div>
                )}
                {userState.isSearching ? (
                    <div className="flex flex-col items-center justify-center h-[60vh] space-y-6">
                        <div className="relative w-32 h-32">
                            <div className="absolute inset-0 border-4 border-gray-800 rounded-full"></div>
                            <div className="absolute inset-0 border-4 border-t-beer-500 rounded-full animate-spin"></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <Logo className="w-16 h-16 animate-pulse" />
                            </div>
                        </div>
                        <div className="text-center">
                            <p className="text-xl font-bold text-white mb-2">Ищем компанию...</p>
                            <p className="text-sm text-gray-400">Сканируем ближайшие 2 км</p>
                        </div>
                    </div>
                ) : buddies.length > 0 ? (
                    <div className="space-y-5 animate-fade-in-up">
                        <div className="flex justify-between items-end px-1">
                             <h3 className="text-gray-400 text-xs uppercase tracking-widest font-bold">Результаты поиска</h3>
                             {userState.isGuest && (
                                 <span className="text-[10px] text-beer-500 bg-beer-500/10 px-2 py-0.5 rounded-full border border-beer-500/20">Гостевой режим</span>
                             )}
                        </div>
                        {buddies
                          .filter(buddy => {
                            // Дополнительная фильтрация на клиенте для надежности
                            if (searchType === 'ads') {
                              return buddy.type === 'ad';
                            } else {
                              return buddy.type === 'user';
                            }
                          })
                          .map((buddy) => (
                        <Suspense key={buddy.id} fallback={<div className="h-40 bg-dark-800 rounded-xl animate-pulse"></div>}>
                          <BuddyCard 
                              buddy={buddy} 
                              onConnect={handleConnect} 
                              onAddFriend={handleAddFriend}
                              onRate={(buddy) => {
                                console.log('⭐ onRate called from BuddyCard:', buddy);
                                hapticFeedback('light');
                                setBuddyToRate(buddy);
                                console.log('✅ buddyToRate set to:', buddy);
                              }}
                              onViewProfile={(telegramId) => {
                                hapticFeedback('light');
                                setViewingProfileTelegramId(telegramId);
                              }}
                          />
                        </Suspense>
                        ))}
                        <div className="h-10"></div>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center h-[60vh] text-center px-6">
                        <div className="bg-dark-800 p-6 rounded-full mb-6 border border-white/5 shadow-xl">
                           <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500"><circle cx="12" cy="12" r="10"/><path d="M16.2 7.8l-2.5 4.9-4.9 2.5 2.5 4.9z"/></svg>
                        </div>
                        <p className="text-gray-400 mb-6 text-lg">Нажмите кнопку поиска сверху, чтобы найти компанию на вечер в вашем районе.</p>
                        <button onClick={startSearch} className="px-8 py-4 bg-beer-600 hover:bg-beer-500 text-black font-bold rounded-full active:scale-95 transition-all shadow-lg shadow-beer-600/20">
                            Найти компанию
                        </button>
                    </div>
                )}
            </>
        )}

        {/* --- CHATS VIEW --- */}
        {currentView === 'chats' && (
            <Suspense fallback={<div className="text-center text-white py-20">Загрузка чатов...</div>}>
              <ChatList 
                  buddies={[...chatBuddies, ...buddies.filter(b => !chatBuddies.some(cb => cb.telegram_id === b.telegram_id))]} 
                  chatHistory={chatHistory} 
                  onOpenChat={handleConnect} 
              />
            </Suspense>
        )}

        {/* --- PROFILE VIEW --- */}
        {currentView === 'profile' && (
            <div className="pb-20">
                {/* Alcohol Warning Banner */}
                <div className="mb-6 bg-red-500/10 border-2 border-red-500/30 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-400">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="8" x2="12" y2="12"></line>
                                <line x1="12" y1="16" x2="12.01" y2="16"></line>
                            </svg>
                        </div>
                        <div className="flex-1">
                            <h4 className="text-red-400 font-bold mb-1 text-sm">Важная информация</h4>
                            <p className="text-xs text-gray-400 mb-2">
                                Чрезмерное употребление алкоголя вредит вашему здоровью. 
                                Используйте сервис ответственно.
                            </p>
                            <div className="flex gap-2 text-[10px]">
                                <a href="/terms.html" target="_blank" className="text-beer-400 underline">Правила</a>
                                <span className="text-gray-600">•</span>
                                <a href="/privacy-policy.html" target="_blank" className="text-beer-400 underline">Конфиденциальность</a>
                            </div>
                        </div>
                    </div>
                </div>
                
                <Suspense fallback={<div className="text-center text-white py-20">Загрузка профиля...</div>}>
                  <ProfileEditor 
                      userState={userState} 
                      setUserState={setUserState} 
                      onSubmit={handleUpdateProfile}
                      isEditing={true}
                      friends={userState.friends}
                  />
                </Suspense>
                
                {/* My Proposals Section */}
                {!userState.isGuest && userState.telegramId && (
                  <div className="mt-6 bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
                    <h4 className="text-purple-400 font-bold mb-3 text-sm flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                      </svg>
                      Мои предложения
                    </h4>
                    <Suspense fallback={<div className="text-center text-gray-400 py-4 text-sm">Загрузка...</div>}>
                      <MyProposals 
                        telegramId={userState.telegramId}
                        onEdit={handleEditProposal}
                        onDelete={(adId) => {
                          if (window.confirm('Вы уверены, что хотите удалить это предложение?')) {
                            handleDeleteProposal(adId);
                          }
                        }}
                      />
                    </Suspense>
                  </div>
                )}

                
                {/* Manual Location Selection */}
                <div className="mt-6 bg-blue-500/5 border border-blue-500/20 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-400">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                        <circle cx="12" cy="10" r="3"></circle>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-blue-400 font-bold mb-1 text-sm">Местоположение для предложения</h4>
                      <p className="text-xs text-gray-400 mb-3">
                        {userState.location 
                          ? `📍 ${userState.location.lat.toFixed(4)}, ${userState.location.lng.toFixed(4)}` 
                          : 'Не установлено (обязательно для создания предложения)'}
                      </p>
                      <button
                        onClick={() => {
                          hapticFeedback('light');
                          setShowMapView(true);
                          setSelectLocationMode(true);
                        }}
                        className="w-full bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 font-semibold py-2 px-4 rounded-lg transition-colors text-sm"
                      >
                        📍 Выбрать на карте
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* My Ratings Section */}
                {!userState.isGuest && userState.telegramId && (
                  <div className="mt-6 bg-yellow-500/5 border border-yellow-500/20 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-yellow-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-yellow-400">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-yellow-400 font-bold mb-1 text-sm">Мой рейтинг</h4>
                        <p className="text-xs text-gray-400 mb-3">
                          ⭐ {userState.rating?.toFixed(1) || '0.0'} • Посмотреть все оценки
                        </p>
                        <button
                          onClick={() => {
                            hapticFeedback('light');
                            setShowUserRatings(true);
                          }}
                          className="w-full bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 font-semibold py-2 px-4 rounded-lg transition-colors text-sm"
                        >
                          ⭐ Посмотреть отзывы
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Invite Friends Section */}
                {!userState.isGuest && (
                  <div className="mt-6 bg-purple-500/5 border border-purple-500/20 rounded-xl p-4">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-purple-500">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                          <circle cx="9" cy="7" r="4"></circle>
                          <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-purple-400 font-bold mb-1 text-sm">Пригласи друзей</h4>
                        <p className="text-xs text-gray-400">
                          {userState.referralCount || 0} / 10 приглашений • 
                          {userState.isPremium 
                            ? ' VIP активен!' 
                            : ` Еще ${10 - (userState.referralCount || 0)} до VIP`}
                        </p>
                      </div>
                    </div>
                    <button 
                      onClick={() => setShowInviteModal(true)}
                      className="w-full py-3 bg-gradient-to-r from-purple-600 to-purple-400 hover:from-purple-500 hover:to-purple-300 text-white font-bold text-sm rounded-lg transition-colors"
                    >
                      Пригласить друзей
                    </button>
                  </div>
                )}

                {/* Verification Section - только если заполнена анкета */}
                {!userState.isVerified && !userState.isGuest && userState.drinkingPreferences && (
                  <div className="mt-6 bg-green-500/5 border border-green-500/20 rounded-xl p-4">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-500">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                          <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-green-400 font-bold mb-1 text-sm">Подтверди личность</h4>
                        <p className="text-xs text-gray-400">Получи бейдж "Проверен" и больше доверия от других пользователей</p>
                      </div>
                    </div>
                    <button 
                      onClick={() => setShowVerificationModal(true)}
                      className="w-full py-3 bg-green-600 hover:bg-green-500 text-white font-bold text-sm rounded-lg transition-colors"
                    >
                      Пройти верификацию
                    </button>
                  </div>
                )}

                {/* Подсказка для новичков без анкеты */}
                {!userState.isGuest && !userState.drinkingPreferences && (
                  <div className="mt-6 bg-beer-500/5 border border-beer-500/20 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-beer-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-beer-500">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="16" x2="12" y2="12"></line>
                          <line x1="12" y1="8" x2="12.01" y2="8"></line>
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-beer-400 font-bold mb-1 text-sm">Создай анкету</h4>
                        <p className="text-xs text-gray-400">Заполни профиль, чтобы другие могли тебя найти и получить доступ к верификации</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-10 border-t border-gray-800 pt-8">
                     <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4 text-center">
                         <h4 className="text-red-400 font-bold mb-2 text-sm">Опасная зона</h4>
                         <button onClick={handleDeleteProfile} className="w-full py-3 bg-red-500/10 text-red-500 font-bold text-sm rounded-lg hover:bg-red-500/20 transition-colors flex items-center justify-center gap-2">
                             <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                             {userState.isGuest ? 'Выйти из режима гостя' : 'Удалить анкету и выйти'}
                         </button>
                         <p className="text-[10px] text-gray-500 mt-2">
                             Это действие безвозвратно удалит вашу анкету и историю сообщений с этого устройства.
                         </p>
                     </div>
                </div>
            </div>
        )}

      </main>

      {/* Chat Overlay */}
      {isChatActive && activeBuddy && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"><div className="text-white">Загрузка чата...</div></div>}>
          <ChatWindow 
            buddy={activeBuddy}
            messages={chatHistory[getChatKey(activeBuddy.telegram_id)] || []}
            onSendMessage={handleSendMessage}
            onClose={handleCloseChat}
            isTyping={isBuddyTyping}
            onViewProfile={(telegramId) => {
              hapticFeedback('light');
              setViewingProfileTelegramId(telegramId);
            }}
            onRate={(buddy) => {
              console.log('⭐ onRate called from ChatWindow:', buddy);
              hapticFeedback('light');
              setBuddyToRate(buddy);
              console.log('✅ buddyToRate set to:', buddy);
            }}
          />
        </Suspense>
      )}

      {/* Global Chat Overlay */}
      {currentView === 'global-chat' && userState.telegramId && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"><div className="text-white">Загрузка общего чата...</div></div>}>
          <GlobalChat
            myTelegramId={userState.telegramId}
            myName={userState.name}
            myPhotoUrl={userState.photoUrl}
            onClose={() => setCurrentView('search')}
          />
        </Suspense>
      )}

      {/* Bottom Navigation */}
      {!isChatActive && (
          <Suspense fallback={<div className="fixed bottom-0 w-full h-20 bg-dark-900/90 z-40"></div>}>
            {currentView !== 'global-chat' && (
              <BottomNav 
                currentView={currentView} 
                setView={(view) => { hapticFeedback('light'); setCurrentView(view); }} 
                unreadCount={notificationCounts.unreadMessages}
                friendRequestsCount={notificationCounts.friendRequests}
                onCreateProposal={() => {
                  try {
                    hapticFeedback('light');
                    // Проверяем условия при клике
                    if (userState.isGuest) {
                      window.Telegram?.WebApp?.showAlert('Для создания предложения необходимо создать профиль');
                      return;
                    }
                    if (!userState.name) {
                      window.Telegram?.WebApp?.showAlert('Для создания предложения необходимо указать имя в профиле');
                      return;
                    }
                    if (!userState.photoUrl && !userState.photoBase64) {
                      window.Telegram?.WebApp?.showAlert('Для создания предложения необходимо загрузить фото в профиле');
                      return;
                    }
                    // Очищаем предыдущие данные перед открытием модалки
                    setPendingProposalData(null);
                    setPendingProposalLocation(null);
                    setEditingProposal(null);
                    setShowCreateProposalModal(true);
                  } catch (error) {
                    console.error('Ошибка при открытии модалки создания предложения:', error);
                    window.Telegram?.WebApp?.showAlert('Произошла ошибка. Попробуйте еще раз.');
                  }
                }}
              />
            )}
          </Suspense>
      )}

      {/* Modals */}
      {showFiltersModal && userState.searchPreferences && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center"><div className="text-white">Загрузка фильтров...</div></div>}>
          <SearchFilters 
            preferences={userState.searchPreferences}
            onChange={(prefs) => setUserState(prev => ({ ...prev, searchPreferences: prefs }))}
            onClose={() => setShowFiltersModal(false)}
            onApply={() => {
              // Автоматически запускаем поиск при применении фильтров
              if (userState.location && !userState.isSearching) {
                console.log('🔍 Применены фильтры, запускаем поиск...');
                setTimeout(() => {
                  startSearch();
                }, 200);
              }
            }}
          />
        </Suspense>
      )}

      {showVerificationModal && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center"><div className="text-white">Загрузка верификации...</div></div>}>
          <VerificationModal 
            onClose={() => setShowVerificationModal(false)}
            onSubmit={handleVerificationSubmit}
          />
        </Suspense>
      )}

      {buddyToRate && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center"><div className="text-white">Загрузка рейтинга...</div></div>}>
          <RatingModal 
            buddy={buddyToRate}
            onClose={() => {
              console.log('❌ Rating modal closed');
              setBuddyToRate(null);
            }}
            onSubmit={(rating, comment) => {
              console.log('✅ Rating modal submit:', { rating, comment, buddy: buddyToRate });
              handleRatingSubmit(rating, comment);
            }}
          />
        </Suspense>
      )}

      {showInviteModal && userState.referralCode && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center"><div className="text-white">Загрузка приглашений...</div></div>}>
          <InviteFriendsModal 
            referralCode={userState.referralCode}
            referralCount={userState.referralCount || 0}
            isPremium={userState.isPremium}
            premiumUntil={userState.premiumUntil}
            onClose={() => setShowInviteModal(false)}
          />
        </Suspense>
      )}

      {showCreateProposalModal && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center"><div className="text-white">Загрузка...</div></div>}>
          <CreateProposalModal
            userTelegramId={userState.telegramId}
            editingProposal={editingProposal}
            onClose={() => {
              // ВАЖНО: Не очищаем pendingProposalData при закрытии модалки,
              // так как пользователь может вернуться после выбора локации
              // Данные очищаются только после успешного создания/обновления предложения
              setShowCreateProposalModal(false);
              setEditingProposal(null);
              // НЕ очищаем pendingProposalData и pendingProposalLocation здесь!
            }}
            onSubmit={editingProposal ? handleUpdateProposal : handleCreateProposal}
            currentLocation={pendingProposalLocation || userState.location}
            onSelectLocation={(formData) => {
              // Сохраняем данные формы и закрываем модалку для выбора локации на карте
              setPendingProposalData(formData);
              setShowCreateProposalModal(false);
              setCurrentView('search');
            }}
            savedFormData={pendingProposalData}
            onFormDataCleared={() => {
              setPendingProposalData(null);
              setPendingProposalLocation(null);
            }}
          />
        </Suspense>
      )}

      {showMapView && (
        <Suspense fallback={
          <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center">
            <div className="text-white text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
              <p>Загрузка карты...</p>
            </div>
          </div>
        }>
          <MapView 
            buddies={mapBuddies}
            userLocation={userState.location}
            onClose={() => {
              setShowMapView(false);
              setSelectLocationMode(false);
            }}
            onLocationSelect={selectLocationMode ? (lat: number, lng: number) => {
              handleManualLocationSelect(lat, lng);
            } : undefined}
            selectLocationMode={selectLocationMode}
            onViewModeChange={(mode) => {
              // Обновляем данные на карте при переключении режима
              loadMapBuddies(mode);
            }}
            onWriteToUser={(telegramId, name) => {
              // Находим пользователя в mapBuddies и открываем чат
              const buddy = mapBuddies.find(b => b.telegram_id === telegramId);
              if (buddy) {
                setActiveBuddy(buddy);
                setCurrentView('chats');
                setShowMapView(false);
              } else {
                // Если не нашли, создаем временный объект buddy и загружаем данные профиля
                fetch(`/api/profile/${telegramId}`)
                  .then(res => res.json())
                  .then(data => {
                const tempBuddy: Buddy = {
                  id: `temp-${telegramId}`,
                  name: name,
                  distance: '',
                  drinkingPreferences: '',
                  mood: '',
                  locationType: '',
                  description: '',
                  lastActive: '',
                      telegram_id: telegramId,
                      avatarUrl: data.user?.photo_url || `https://picsum.photos/seed/${name}${telegramId}/200/200`
                };
                setActiveBuddy(tempBuddy);
                setCurrentView('chats');
                setShowMapView(false);
                    
                    // Загружаем историю чата
                    const tgUser = getTelegramUser();
                    const myTelegramId = tgUser?.id || userState.telegramId;
                    if (myTelegramId) {
                      fetch(`/api/chat/history?user1_telegram_id=${myTelegramId}&user2_telegram_id=${telegramId}`)
                        .then(res => res.json())
                        .then(historyData => {
                          if (historyData.success && historyData.messages) {
                            const formattedMessages: ChatMessage[] = historyData.messages.map((msg: any) => ({
                              id: msg.id.toString(),
                              sender: msg.sender === myTelegramId.toString() ? 'user' : 'buddy',
                              text: msg.text,
                              timestamp: msg.timestamp
                            }));
                            const chatKey = getChatKey(telegramId);
                            setChatHistory(prev => ({ ...prev, [chatKey]: formattedMessages }));
                          } else {
                            const chatKey = getChatKey(telegramId);
                            setChatHistory(prev => ({ ...prev, [chatKey]: [] }));
                          }
                        })
                        .catch(() => {
                          const chatKey = getChatKey(telegramId);
                          setChatHistory(prev => ({ ...prev, [chatKey]: [] }));
                        });
                    }
                  })
                  .catch(() => {
                    // Если не удалось загрузить профиль, создаем без фото
                    const tempBuddy: Buddy = {
                      id: `temp-${telegramId}`,
                      name: name,
                      distance: '',
                      drinkingPreferences: '',
                      mood: '',
                      locationType: '',
                      description: '',
                      lastActive: '',
                      telegram_id: telegramId,
                      avatarUrl: `https://picsum.photos/seed/${name}${telegramId}/200/200`
                    };
                    setActiveBuddy(tempBuddy);
                    setCurrentView('chats');
                    setShowMapView(false);
                  });
              }
            }}
            onViewProfile={(telegramId) => {
              hapticFeedback('light');
              setViewingProfileTelegramId(telegramId);
              setShowMapView(false);
            }}
          />
        </Suspense>
      )}

      {showUserRatings && userState.telegramId && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center"><div className="text-white">Загрузка рейтингов...</div></div>}>
          <UserRatings 
            telegramId={userState.telegramId}
            onClose={() => setShowUserRatings(false)}
          />
        </Suspense>
      )}

      {viewingProposal && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center"><div className="text-white">Загрузка предложения...</div></div>}>
          <ProposalView
            proposal={viewingProposal}
            onClose={() => setViewingProposal(null)}
            onViewProfile={(telegramId) => {
              hapticFeedback('light');
              setViewingProposal(null);
              setViewingProfileTelegramId(telegramId);
            }}
            onWriteMessage={(buddy) => {
              hapticFeedback('light');
              setViewingProposal(null);
              // Для предложений открываем чат напрямую, минуя проверку типа
              setActiveBuddy(buddy);
              
              // Load chat history from server
              const tgUser = getTelegramUser();
              const myTelegramId = tgUser?.id || userState.telegramId;
              
              if (myTelegramId && buddy.telegram_id) {
                fetch(`/api/chat/history?user1_telegram_id=${myTelegramId}&user2_telegram_id=${buddy.telegram_id}`)
                  .then(res => res.json())
                  .then(data => {
                    if (data.success && data.messages) {
                      const formattedMessages: ChatMessage[] = data.messages.map((msg: any) => ({
                        id: msg.id.toString(),
                        sender: msg.sender === myTelegramId.toString() ? 'user' : 'buddy',
                        text: msg.text,
                        timestamp: msg.timestamp
                      }));
                      // ВАЖНО: Используем telegram_id как ключ для истории чата
                      const chatKey = getChatKey(buddy.telegram_id);
                      setChatHistory(prev => ({ ...prev, [chatKey]: formattedMessages }));
                    } else {
                      const chatKey = getChatKey(buddy.telegram_id);
                      setChatHistory(prev => ({ ...prev, [chatKey]: [] }));
                    }
                  })
                  .catch(() => {
                    const chatKey = getChatKey(buddy.telegram_id);
                    setChatHistory(prev => ({ ...prev, [chatKey]: [] }));
                  });
              } else {
                const chatKey = getChatKey(buddy.telegram_id);
                setChatHistory(prev => ({ ...prev, [chatKey]: [] }));
              }
              
              setCurrentView('chats');
            }}
            onRate={(buddy) => {
              console.log('⭐ onRate called from ProposalView:', buddy);
              hapticFeedback('light');
              setBuddyToRate(buddy);
            }}
          />
        </Suspense>
      )}

      {viewingProfileTelegramId && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center"><div className="text-white">Загрузка анкеты...</div></div>}>
          <UserProfileView
            telegramId={viewingProfileTelegramId}
            myTelegramId={getTelegramUser()?.id || userState.telegramId}
            friendStatus={
              userState.friends?.find(f => f.telegram_id === viewingProfileTelegramId)?.status === 'accepted' 
                ? 'friend' 
                : userState.friends?.find(f => f.telegram_id === viewingProfileTelegramId)?.status === 'pending'
                ? 'pending'
                : (getTelegramUser()?.id || userState.telegramId) === viewingProfileTelegramId
                ? 'self'
                : null
            }
            onClose={() => setViewingProfileTelegramId(null)}
            onWriteMessage={(telegramId, name) => {
              // Находим пользователя и открываем чат
              const buddy = buddies.find(b => b.telegram_id === telegramId) || 
                           mapBuddies.find(b => b.telegram_id === telegramId);
              if (buddy) {
                setActiveBuddy(buddy);
                setCurrentView('chats');
                setViewingProfileTelegramId(null);
              } else {
                // Создаем временный объект buddy и загружаем данные профиля
                // Загружаем фото профиля из API
                fetch(`/api/profile/${telegramId}`)
                  .then(res => res.json())
                  .then(data => {
                const tempBuddy: Buddy = {
                  id: `temp-${telegramId}`,
                  name: name,
                  distance: '',
                  drinkingPreferences: '',
                  mood: '',
                  locationType: '',
                  description: '',
                  lastActive: '',
                      telegram_id: telegramId,
                      avatarUrl: data.user?.photo_url || `https://picsum.photos/seed/${name}${telegramId}/200/200`
                };
                setActiveBuddy(tempBuddy);
                setCurrentView('chats');
                setViewingProfileTelegramId(null);
                    
                    // Загружаем историю чата
                    const tgUser = getTelegramUser();
                    const myTelegramId = tgUser?.id || userState.telegramId;
                    if (myTelegramId) {
                      fetch(`/api/chat/history?user1_telegram_id=${myTelegramId}&user2_telegram_id=${telegramId}`)
                        .then(res => res.json())
                        .then(historyData => {
                          if (historyData.success && historyData.messages) {
                            const formattedMessages: ChatMessage[] = historyData.messages.map((msg: any) => ({
                              id: msg.id.toString(),
                              sender: msg.sender === myTelegramId.toString() ? 'user' : 'buddy',
                              text: msg.text,
                              timestamp: msg.timestamp
                            }));
                            const chatKey = getChatKey(telegramId);
                            setChatHistory(prev => ({ ...prev, [chatKey]: formattedMessages }));
                          } else {
                            const chatKey = getChatKey(telegramId);
                            setChatHistory(prev => ({ ...prev, [chatKey]: [] }));
                          }
                        })
                        .catch(() => {
                          const chatKey = getChatKey(telegramId);
                          setChatHistory(prev => ({ ...prev, [chatKey]: [] }));
                        });
                    }
                  })
                  .catch(() => {
                    // Если не удалось загрузить профиль, создаем без фото
                    const tempBuddy: Buddy = {
                      id: `temp-${telegramId}`,
                      name: name,
                      distance: '',
                      drinkingPreferences: '',
                      mood: '',
                      locationType: '',
                      description: '',
                      lastActive: '',
                      telegram_id: telegramId,
                      avatarUrl: `https://picsum.photos/seed/${name}${telegramId}/200/200`
                    };
                    setActiveBuddy(tempBuddy);
                    setCurrentView('chats');
                    setViewingProfileTelegramId(null);
                  });
              }
            }}
            onRate={(telegramId, name) => {
              console.log('⭐ onRate called from UserProfileView:', { telegramId, name });
              hapticFeedback('light');
              // Создаем временный объект Buddy для оценки
              const tempBuddy: Buddy = {
                id: `temp-${telegramId}`,
                name: name,
                distance: '',
                drinkingPreferences: '',
                mood: '',
                locationType: '',
                description: '',
                lastActive: '',
                telegram_id: telegramId
              };
              console.log('✅ Creating tempBuddy:', tempBuddy);
              setBuddyToRate(tempBuddy);
              console.log('✅ buddyToRate set to:', tempBuddy);
            }}
          />
        </Suspense>
      )}
      
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .pb-safe {
            padding-bottom: env(safe-area-inset-bottom);
        }
      `}</style>
    </div>
  );
};

export default App;